import { idempotencyKeySessionStorage } from '@ctct/infrastructure-layer';

import { IdempotencyKeyService } from './services/IdempotencyKeyService';
import { PaymentButtonService } from './services/PaymentButtonService';
import { TermsAndConditionsService } from './services/TermsAndConditionsService';
import { TranslationsService } from './services/TranslationsService';
import type { Init } from './types';
import type { MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import { WithApi } from '../WithApi';

export type PaymentButtonData = MergedDataReturnType<
  [PaymentButtonService, TranslationsService, TermsAndConditionsService]
> & {
  isLoading(): boolean;
};

export type PaymentButtonHandlers = MergedHandlersReturnType<
  [PaymentButtonService, TranslationsService, TermsAndConditionsService]
>;

type TApi = ReturnType<PaymentButtonService['api']> &
  ReturnType<TranslationsService['api']> &
  ReturnType<TermsAndConditionsService['api']> &
  ReturnType<IdempotencyKeyService['api']>;

export class PaymentButtonManager extends WithApi<TApi> {
  public paymentButtonInit = ({
    pageStore,
    queries,
    mutations,
    t,
    isMobile,
    hasPendingExternalRedirect,
    billingStrategy,
    accountId,
    isSingleBillChildAccount,
  }: Init) => {
    const [partnerName, billingInfoQuery] = queries;
    const [payForPlan] = mutations;

    const termsAndConditionsService = new TermsAndConditionsService(pageStore, partnerName.data);
    const paymentButtonService = new PaymentButtonService(
      pageStore,
      payForPlan,
      t,
      termsAndConditionsService.getTermsStrategy,
      isMobile,
      isSingleBillChildAccount
    );
    const translations = new TranslationsService(billingStrategy, pageStore, billingInfoQuery);
    const idempotencyKeyService = new IdempotencyKeyService(accountId, idempotencyKeySessionStorage);

    this.initApi({
      ...paymentButtonService.api(),
      ...translations.api(),
      ...idempotencyKeyService.api(),
    });

    return {
      status: 'loaded',
      data: {
        ...paymentButtonService.data(),
        ...translations.data(),
        ...termsAndConditionsService.data(),
        isLoading: () =>
          partnerName.isPending || billingInfoQuery.isPending || payForPlan.isPending || hasPendingExternalRedirect,
      },
      handlers: {
        ...paymentButtonService.handlers(),
        ...translations.handlers(),
        ...termsAndConditionsService.handlers(),
      },
    } as const;
  };
}
